/**
 * Pure GPIO Extender CDC protocol helpers.
 */

export const USB_VENDOR_ID = 0x0483;
export const USB_PRODUCT_ID = 0xA26A;

export const PIN_COUNT = 10;
export const CDC_PACKET_SIZE = 64;
export const CDC_COMMAND_OVERHEAD = 4; // ~ + command + CRLF
export const CDC_MAX_PARAMS_BYTES = CDC_PACKET_SIZE - CDC_COMMAND_OVERHEAD;

export const PWM_MIN_FREQUENCY = 1;
export const PWM_MAX_FREQUENCY = 20_000;
export const PWM_MIN_DUTY = 0;
export const PWM_MAX_DUTY = 100;

export const WS2812_MAX_PIXELS = CDC_MAX_PARAMS_BYTES / 2;
export const SCRIPT_MAX_BYTES = 255;
export const SCRIPT_CHUNK_BYTES = CDC_MAX_PARAMS_BYTES;

// Connector pin number to MCU GPIO name.
export const PIN_GPIO_MAP = Object.freeze({
    1: 'PA4',
    2: 'PA2',
    3: 'PA0',
    4: 'PA7',
    5: 'PA6',
    6: 'PA3',
    7: 'PA1',
    8: 'PA13',
    9: 'PB1',
    10: 'PA14'
});

export const PIN_MODES = Object.freeze({
    I: { name: 'Input', pins: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
    O: { name: 'Output', pins: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
    P: { name: 'PWM', pins: [2, 3, 6, 7] },
    A: { name: 'Analog', pins: [1, 2, 3, 4, 5, 6, 7, 9] },
    D: { name: 'DHT', pins: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
    W: { name: 'WS2812', pins: [4] },
    C: { name: 'Capture', pins: [4, 5, 9] },
    T: { name: 'UART TX', pins: [2] },
    R: { name: 'UART RX', pins: [6] },
    '-': { name: 'Keep unchanged', pins: [8, 10] }
});

const VALID_MODE_CODES = new Set(Object.keys(PIN_MODES));

export function connectorPinToProtocol(pin) {
    if (!Number.isInteger(pin) || pin < 1 || pin > PIN_COUNT) {
        throw new RangeError(`Pin must be an integer from 1 to ${PIN_COUNT}`);
    }
    return pin === PIN_COUNT ? 0 : pin;
}

export function validateModes(modes) {
    if (typeof modes !== 'string' || modes.length !== PIN_COUNT) {
        throw new Error(`Modes must be exactly ${PIN_COUNT} characters`);
    }
    if (![...modes].every((mode, index) => {
        return VALID_MODE_CODES.has(mode) && PIN_MODES[mode].pins.includes(index + 1);
    })) {
        throw new Error('Modes contain a mode unsupported by its pin');
    }
    return modes;
}

export function validatePwmDuty(value) {
    const text = String(value).trim();
    if (!/^(?:0|[1-9]\d?|100)$/.test(text)) {
        throw new RangeError(`PWM duty must be an integer from ${PWM_MIN_DUTY} to ${PWM_MAX_DUTY}`);
    }
    return Number(text);
}

export function validatePwmFrequency(value) {
    const text = String(value).trim();
    if (!/^[1-9]\d*$/.test(text)) {
        throw new RangeError(`PWM frequency must be an integer from ${PWM_MIN_FREQUENCY} to ${PWM_MAX_FREQUENCY} Hz`);
    }

    const frequency = Number(text);
    if (!Number.isSafeInteger(frequency) || frequency < PWM_MIN_FREQUENCY || frequency > PWM_MAX_FREQUENCY) {
        throw new RangeError(`PWM frequency must be an integer from ${PWM_MIN_FREQUENCY} to ${PWM_MAX_FREQUENCY} Hz`);
    }
    return frequency;
}

export function validateWS2812Hex(hexData) {
    const hex = String(hexData).trim().toUpperCase();
    if (!/^(?:[0-9A-F]{2})+$/.test(hex)) {
        throw new Error('WS2812 data must contain complete hexadecimal palette bytes');
    }
    if (hex.length / 2 > WS2812_MAX_PIXELS) {
        throw new RangeError(`A CDC command supports at most ${WS2812_MAX_PIXELS} WS2812 pixels`);
    }
    return hex;
}

export function validateScript(script) {
    if (typeof script !== 'string') {
        throw new TypeError('Script must be a string');
    }
    if (/[^\x00-\x7F]/.test(script)) {
        throw new Error('Script must contain ASCII characters only');
    }
    if (/[\r\n]/.test(script)) {
        throw new Error('Script must not contain line breaks');
    }
    if (script.length > SCRIPT_MAX_BYTES) {
        throw new RangeError(`Script must not exceed ${SCRIPT_MAX_BYTES} bytes`);
    }
    return script;
}

export function splitScript(script) {
    const validated = validateScript(script);
    const chunks = [];
    for (let offset = 0; offset < validated.length; offset += SCRIPT_CHUNK_BYTES) {
        chunks.push(validated.slice(offset, offset + SCRIPT_CHUNK_BYTES));
    }
    return chunks;
}

export function validateCdcCommand(cmd, params = '') {
    if (typeof cmd !== 'string' || !/^[\x20-\x7E]$/.test(cmd)) {
        throw new Error('CDC command must be one printable ASCII character');
    }

    const text = String(params);
    if (/[\r\n]/.test(text)) {
        throw new Error('CDC command parameters must not contain line breaks');
    }
    if (new TextEncoder().encode(text).length > CDC_MAX_PARAMS_BYTES) {
        throw new RangeError(`CDC command parameters must not exceed ${CDC_MAX_PARAMS_BYTES} bytes`);
    }
    return text;
}

export function isSuccessfulResponse(response, cmd) {
    return response === `${cmd}OK` || response?.startsWith(`${cmd}OK:`) || false;
}
